/**
 * Shared types and pure helpers used by the banner (browser) and the edge Worker.
 * Keep this file dependency-free and side-effect-free.
 */

export * from './rulebook';
export { covers, type Coverage, type SiteFacts } from './coverage';
export { COOKIELESS_ANALYTICS, isCookielessAnalytics } from './cookieless';
export { NO_PERSONAL_DATA, NOTICE_DAYS, SUBPROCESSOR_CHANGES, SUBPROCESSORS, type Subprocessor, type SubprocessorChange } from './subprocessors';
export { ANALYTICS_NEEDS, EXEMPTABLE_ANALYTICS, SHORT_COOKIE_DAYS, analyticsCheck, exemptableTool, exemptionHolds, type AnalyticsCheck, type AnalyticsNeed, type CheckInput } from './analytics';
export { diffSettings, type SettingChange } from './changes';
export { BUILTIN_TEXTS, EN_TEXTS, resolveTexts } from './texts';
import { EN_TEXTS } from './texts';
export { BANNER_LANGS, LANG_ALIASES, RTL_LANGS, WORLD_PLAIN } from './texts-world';
import { WORLD_PLAIN } from './texts-world';
export { BASE_RULEBOOK } from './rulebook-base';
export { EVENT_KIND_LABELS, eventKind, optOutRecord, type EventKind, type OptOutRecord } from './history';
export { EU_MEMBERS, groupOf, LAWS, lawsFor, PLACE_GROUPS, type Law, type PlaceGroup } from './laws';
import type { SitePolicy, Treatment } from './rulebook';
import type { SiteFacts } from './coverage';
import type { AnalyticsCheck } from './analytics';

export const CATEGORIES = ['necessary', 'functionality', 'analytics', 'marketing'] as const;
export type Category = (typeof CATEGORIES)[number];
export type OptionalCategory = Exclude<Category, 'necessary'>;

/**
 * How a visitor's region is treated at runtime.
 * optin: block until a choice (the card). optout: allowed by default, GPC honoured, settings link only.
 * none: no banner, everything allowed. notice: UK statistical exception, analytics on by default with an Object link.
 */
export type Regime = 'optin' | 'optout' | 'none' | 'notice' | 'inform';

/** What the site owner picks per region. Resolved to a Regime by the edge. */
export type Mode = 'ask' | 'quiet' | 'notice';
export type Region = 'eu' | 'uk' | 'us' | 'row';
export const REGIONS: readonly Region[] = ['eu', 'uk', 'us', 'row'];
export const REGION_NAMES: Record<Region, string> = { eu: 'Europe', uk: 'United Kingdom', us: 'United States', row: 'Everywhere else' };

export type ConsentAction = 'accept_all' | 'reject_all' | 'custom' | 'gpc' | 'implied';

export type ConsentChoice = Record<Category, boolean>;

export interface BlockRule {
  /** Substring matched against a script/iframe URL (host or full URL). Case-insensitive. */
  match: string;
  category: OptionalCategory;
}

export interface CategoryText {
  name: string;
  description: string;
}

export interface SiteTexts {
  title: string;
  description: string;
  acceptAll: string;
  rejectAll: string;
  settings: string;
  save: string;
  manage: string;
  privacyChoices: string;
  /** Shown when a Global Privacy Control signal was honoured where the law requires a visible confirmation. */
  gpcHonored?: string;
  privacyPolicy: string;
  cookieDeclaration: string;
  always: string;
  blockedEmbed: string;
  loadEmbed: string;
  /** UK notice mode: one line, analytics on by default. */
  notice: string;
  object: string;
  /** Inform mode (Mexico, Canada): tracking runs, one line says what for, and a button turns it off. */
  inform?: string;
  turnOff?: string;
  /** Where the site's own visit statistics run before a choice: one line saying so. */
  statsRunning?: string;
  /** US quiet mode: the opt-out toggle label. */
  doNotSell: string;
  poweredBy: string;
  categories: Record<Category, CategoryText>;
}

export interface SiteTheme {
  position: 'bottom' | 'center' | 'bottom-left' | 'bottom-right';
  primary: string;
  onPrimary: string;
  background: string;
  text: string;
  muted: string;
  radius: string;
  font: string;
}

export interface SiteConfig {
  siteId: string;
  /** Config revision. Changing it triggers re-consent. */
  version: string;
  /**
   * Fingerprint of everything the banner shows (every language's text, theme, links, rules), set
   * when the config is published. Each response records it, and the published config is kept under
   * it, so what any visitor saw can be shown again exactly. Unlike `version`, a restyle changes it.
   */
  snapshot?: string;
  /** Apex domain used for the consent cookie so subdomains share consent. */
  domain: string;
  defaultLang: string;
  texts: Record<string, SiteTexts>;
  theme: SiteTheme;
  rules: BlockRule[];
  autoBlock: boolean;
  /** Per-region behaviour. See Mode. */
  modes: Record<Region, Mode>;
  /** Legacy field from before modes existed. upgradeConfig() converts it. */
  regimes?: { eu: Regime; us: Regime; row: Regime };
  /** "plain" swaps the button labels for ones that say what happens: Allow tracking / Continue without tracking. */
  wording?: 'standard' | 'plain';
  /**
   * Which language wins when the page and the visitor's browser differ. "page" (the default) follows
   * the page's lang; "browser" follows the visitor's browser first, for sites with a multilingual
   * audience on single-language pages.
   */
  langFrom?: 'page' | 'browser';
  /** Free plan: a muted "Powered by Flat Consent" link inside the card. Set by the dashboard on publish. */
  branding?: boolean;
  consentMode: {
    enabled: boolean;
    waitForUpdate: number;
    adsDataRedaction: boolean;
    urlPassthrough: boolean;
    /** Let Google's tags send their cookieless requests before a visitor agrees ("Advanced"). Off by default. */
    advanced?: boolean;
  };
  uet: boolean;
  reconsentDays: number;
  refusalMemoryDays: number;
  privacyUrl?: string;
  cookieDeclarationUrl?: string;
  showBadge: boolean;
  /** The owner's corrections to what the check decided. Keyed by cookieKey() for cookies, by host for outside services. */
  overrides?: Overrides;
  /** How the law is applied: "law" follows the rulebook per place, "strict" asks first everywhere. */
  stance?: SitePolicy['stance'];
  /** Ask first in California as a shield against pixel lawsuits. Off by default. */
  caShield?: boolean;
  /** The owner's own treatment for particular places ("GB": "notice"). */
  placeOverrides?: Record<string, Treatment>;
  /** Facts about the business some laws turn on (US state law thresholds, analytics exemptions). */
  facts?: SiteFacts;
  /** What our site check found about the site's analytics; decides analytics exemptions. Set by the check only. */
  analytics?: AnalyticsCheck;
}

/** A category, or "unknown" when nothing could tell. */
export type Classification = Category | 'unknown';
export interface Overrides {
  cookies: Record<string, Classification>;
  hosts: Record<string, Classification>;
}
export const cookieKey = (name: string, domain: string): string => `${name}|${domain}`;
export const CLASSIFICATIONS: readonly Classification[] = ['necessary', 'functionality', 'analytics', 'marketing', 'unknown'];

/** The check's categories with the owner's corrections applied. Items that were corrected carry `overridden`. */
/** Flat Consent's own cookie and hosts: always required, never reclassified or held, whatever overrides say. */
export function isOwnCookie(name: string): boolean {
  return name.replace(/ \(storage\)$/, '') === 'cmp_consent';
}
export function isOwnHost(host: string): boolean {
  const h = host.toLowerCase();
  return h === 'flatconsent.com' || h.endsWith('.flatconsent.com');
}
const OWN_COOKIE_TEXT = 'Remembers the visitor’s cookie choice, so the banner does not ask again on every page. No personal data.';

export function applyOverrides<C extends { name: string; domain: string; category: string }, H extends { host: string; category: string }>(
  summary: { cookies: C[]; thirdParties: H[] },
  overrides: Overrides | undefined,
): { cookies: (C & { overridden?: boolean; own?: boolean })[]; thirdParties: (H & { overridden?: boolean; own?: boolean })[] } {
  const own = { category: 'necessary', vendor: 'Flat Consent', source: 'database', own: true };
  return {
    cookies: summary.cookies.map((c) => {
      if (isOwnCookie(c.name)) return { ...c, ...own, description: OWN_COOKIE_TEXT };
      const o = overrides?.cookies[cookieKey(c.name, c.domain)];
      return o ? { ...c, category: o, overridden: true } : c;
    }),
    thirdParties: summary.thirdParties.map((t) => {
      if (isOwnHost(t.host)) return { ...t, ...own };
      const o = overrides?.hosts[t.host.toLowerCase()];
      return o ? { ...t, category: o, overridden: true } : t;
    }),
  };
}

/** The cookie list page, regrouped by the owner's corrections. */
export function remapDeclaration(decl: Declaration, overrides: Overrides | undefined): Declaration {
  const categories: Declaration['categories'] = { necessary: [], functionality: [], analytics: [], marketing: [], unknown: [] };
  for (const [cat, list] of Object.entries(decl.categories) as [Classification, DeclarationEntry[]][]) {
    for (const e of list) {
      if (isOwnCookie(e.name)) categories.necessary.push({ ...e, vendor: 'Flat Consent', description: OWN_COOKIE_TEXT });
      else categories[overrides?.cookies[cookieKey(e.name, e.domain)] ?? cat].push(e);
    }
  }
  for (const list of Object.values(categories)) list.sort((a, b) => a.name.localeCompare(b.name));
  return { ...decl, categories };
}

export interface ResolvedConfig extends SiteConfig {
  regime: Regime;
  country: string;
  /** The place the visitor resolved to ("FR", "US-CA") and the rulebook release applied. */
  place?: string;
  rulebook?: string;
  /** Show a privacy choices link even without a footer link (opt-out laws require one). */
  link?: boolean;
  /** Visibly confirm an honoured GPC signal. */
  gpcConfirm?: boolean;
  /** GPC is an opt-out here. */
  gpc?: boolean;
  /** The banner may not be a bottom bar. */
  noBar?: boolean;
  /** Categories that run before a choice because the law exempts them here (see Applied.preRun). */
  preRun?: 'analytics'[];
  /** Language the edge picked for this visitor, and its writing direction. */
  lang?: string;
  dir?: 'ltr' | 'rtl';
}

/** What the banner posts to the edge. */
export interface LogPayload {
  site: string;
  id: string;
  ts: number;
  action: ConsentAction;
  categories: ConsentChoice;
  version: string;
  lang: string;
  gpc: boolean;
  /** The published config's snapshot id: exactly what this visitor was shown. */
  snapshot?: string;
  /** Banner runtime version that drew it. */
  bv?: string;
}

/** What the edge stores. No IP address in any form, no raw user agent. */
export interface ConsentEvent extends LogPayload {
  receivedAt: number;
  country: string;
  uaFamily: string;
  /** The place the rules came from ("US-CA") and the rulebook release in force. */
  place: string;
  rulebook: string;
}

/** One row of a site's public cookie declaration. */
export interface DeclarationEntry {
  name: string;
  domain: string;
  kind: 'cookie' | 'localStorage';
  firstParty: boolean;
  vendor: string;
  description: string;
  retention: string;
}

/** Published to KV as `decl:<siteId>` by the scanner; rendered by the edge at /declaration/:siteId. */
export interface Declaration {
  siteId: string;
  scanId: string;
  scannedUrl: string;
  generatedAt: number;
  pagesCrawled: number;
  categories: Record<Category | 'unknown', DeclarationEntry[]>;
}

/** EU/EEA + UK + Switzerland: opt-in regimes. */
export const OPTIN_COUNTRIES: ReadonlySet<string> = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT',
  'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', // EU
  'IS', 'LI', 'NO', // EEA
  'GB', 'CH', // UK, Switzerland
]);

/** Countries outside Europe whose law is opt-in in practice (Brazil LGPD guidance). Treated as the EU region. */
const STRICT_ROW: ReadonlySet<string> = new Set(['BR']);

export function regionFor(country: string): Region {
  const c = country.toUpperCase();
  if (c === 'GB') return 'uk';
  if (OPTIN_COUNTRIES.has(c) || STRICT_ROW.has(c)) return 'eu';
  if (c === 'US') return 'us';
  return 'row';
}

/** The banner's runtime behaviour for a treatment. */
export function regimeForTreatment(t: Treatment): Regime {
  return t === 'ask' ? 'optin' : t === 'optout' ? 'optout' : t === 'notice' ? 'notice' : t === 'inform' ? 'inform' : 'none';
}

export function regimeForMode(mode: Mode, region: Region): Regime {
  if (mode === 'ask') return 'optin';
  if (mode === 'notice') return 'notice';
  return region === 'us' ? 'optout' : 'none';
}

export function regimeFor(config: Pick<SiteConfig, 'modes' | 'regimes'>, country: string): Regime {
  const region = regionFor(country);
  const modes = config.modes ?? modesFromRegimes(config.regimes);
  return regimeForMode(modes[region], region);
}

function modesFromRegimes(r: SiteConfig['regimes'] | undefined): Record<Region, Mode> {
  const m = (x: Regime | undefined): Mode => (x === 'optin' ? 'ask' : x === 'notice' ? 'notice' : 'quiet');
  return { eu: m(r?.eu ?? 'optin'), uk: m(r?.eu ?? 'optin'), us: m(r?.us ?? 'optout'), row: m(r?.row ?? 'none') };
}

/** Brings a stored config up to the current shape. Safe to call on every read. */
export function upgradeConfig(c: SiteConfig): SiteConfig {
  const out: SiteConfig = { ...c };
  if (!out.modes) out.modes = modesFromRegimes(c.regimes);
  delete out.regimes;
  if (!out.wording) out.wording = 'standard';
  if (out.branding === undefined) out.branding = false;
  if (!out.overrides) out.overrides = { cookies: {}, hosts: {} };
  if (!out.stance) {
    // Region modes predate the rulebook. Sites move to following the law; a UK notice choice is kept,
    // since that is a real option the law allows and the owner picked it.
    out.stance = 'law';
    out.placeOverrides = { ...(out.modes?.uk === 'notice' ? { GB: 'notice' as Treatment } : {}), ...(out.placeOverrides ?? {}) };
  }
  if (out.caShield === undefined) out.caShield = false;
  if (!out.placeOverrides) out.placeOverrides = {};
  return out;
}


export const DEFAULT_THEME: SiteTheme = {
  position: 'bottom-left',
  primary: '#111827',
  onPrimary: '#ffffff',
  background: '#ffffff',
  text: '#111827',
  muted: '#6b7280',
  radius: '12px',
  font: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
};

/** Trackers most small sites use. Site-specific rules are appended to this list. */
export const BUILTIN_RULES: BlockRule[] = [
  { match: 'googletagmanager.com/gtag/js', category: 'analytics' },
  { match: 'google-analytics.com', category: 'analytics' },
  { match: 'analytics.google.com', category: 'analytics' },
  { match: 'hotjar.com', category: 'analytics' },
  { match: 'clarity.ms', category: 'analytics' },
  { match: 'mixpanel.com', category: 'analytics' },
  { match: 'segment.com', category: 'analytics' },
  { match: 'segment.io', category: 'analytics' },
  { match: 'fullstory.com', category: 'analytics' },
  { match: 'mouseflow.com', category: 'analytics' },
  { match: 'connect.facebook.net', category: 'marketing' },
  { match: 'facebook.com/tr', category: 'marketing' },
  { match: 'googleadservices.com', category: 'marketing' },
  { match: 'doubleclick.net', category: 'marketing' },
  { match: 'googlesyndication.com', category: 'marketing' },
  { match: 'snap.licdn.com', category: 'marketing' },
  { match: 'ads-twitter.com', category: 'marketing' },
  { match: 'analytics.tiktok.com', category: 'marketing' },
  { match: 'pinimg.com/ct', category: 'marketing' },
  { match: 'bat.bing.com', category: 'marketing' },
  { match: 'redditstatic.com/ads', category: 'marketing' },
  { match: 'alb.reddit.com', category: 'marketing' },
  { match: 'sc-static.net/scevent', category: 'marketing' },
  { match: 'tr.snapchat.com', category: 'marketing' },
  { match: 'static.criteo.net', category: 'marketing' },
  { match: 'dynamic.criteo.com', category: 'marketing' },
  { match: 'cdn.taboola.com', category: 'marketing' },
  { match: 'amplify.outbrain.com', category: 'marketing' },
  { match: 'amazon-adsystem.com', category: 'marketing' },
  { match: 'a.quora.com', category: 'marketing' },
  { match: 's.yimg.com/wi/ytc.js', category: 'marketing' },
  { match: 's.adroll.com', category: 'marketing' },
  { match: 'hs-scripts.com', category: 'marketing' },
  { match: 'hsforms.net', category: 'marketing' },
  { match: 'intercom.io', category: 'functionality' },
  { match: 'intercomcdn.com', category: 'functionality' },
  { match: 'crisp.chat', category: 'functionality' },
  { match: 'youtube.com/embed', category: 'marketing' },
  { match: 'youtube-nocookie.com', category: 'marketing' },
  { match: 'player.vimeo.com', category: 'marketing' },
  { match: 'google.com/maps/embed', category: 'marketing' },
  { match: 'open.spotify.com/embed', category: 'marketing' },
];

export function defaultConfig(siteId: string, domain: string): SiteConfig {
  return {
    siteId,
    version: '1',
    domain,
    defaultLang: 'en',
    texts: { en: EN_TEXTS },
    theme: DEFAULT_THEME,
    rules: [],
    autoBlock: true,
    modes: { eu: 'ask', uk: 'ask', us: 'quiet', row: 'quiet' },
    stance: 'law',
    caShield: false,
    placeOverrides: {},
    wording: 'standard',
    branding: false,
    consentMode: { enabled: true, waitForUpdate: 500, adsDataRedaction: true, urlPassthrough: false },
    uet: true,
    reconsentDays: 182,
    refusalMemoryDays: 182,
    showBadge: false,
  };
}

// ---------- generated copy ----------

export type FoundCategories = { analytics: boolean; marketing: boolean; functionality: boolean };

interface CopyTemplate {
  first: string;
  second: string; // contains {list}
  parts: Record<keyof FoundCategories, string>;
  and: string;
}

const COPY: Record<string, CopyTemplate> = {
  en: { first: 'We use cookies to run this site.', second: 'With your permission we also {list}.', parts: { analytics: 'measure visits', marketing: 'show relevant ads', functionality: 'remember your preferences' }, and: 'and' },
  de: { first: 'Wir verwenden Cookies, um diese Website zu betreiben.', second: 'Mit Ihrer Erlaubnis nutzen wir sie auch, um {list}.', parts: { analytics: 'Zugriffe zu messen', marketing: 'relevante Werbung zu zeigen', functionality: 'Ihre Einstellungen zu speichern' }, and: 'und' },
  fr: { first: 'Nous utilisons des cookies pour faire fonctionner ce site.', second: 'Avec votre accord, nous les utilisons aussi pour {list}.', parts: { analytics: 'mesurer l’audience', marketing: 'afficher des publicités pertinentes', functionality: 'mémoriser vos préférences' }, and: 'et' },
  es: { first: 'Usamos cookies para que este sitio funcione.', second: 'Con tu permiso también las usamos para {list}.', parts: { analytics: 'medir las visitas', marketing: 'mostrar anuncios relevantes', functionality: 'recordar tus preferencias' }, and: 'y' },
  it: { first: 'Usiamo i cookie per far funzionare questo sito.', second: 'Con il tuo consenso li usiamo anche per {list}.', parts: { analytics: 'misurare le visite', marketing: 'mostrare annunci pertinenti', functionality: 'ricordare le tue preferenze' }, and: 'e' },
  nl: { first: 'We gebruiken cookies om deze site te laten werken.', second: 'Met uw toestemming gebruiken we ze ook om {list}.', parts: { analytics: 'bezoeken te meten', marketing: 'relevante advertenties te tonen', functionality: 'uw voorkeuren te onthouden' }, and: 'en' },
};

/** Two sentences: what runs anyway, then only the categories the scan actually found. */
export function generatedDescription(lang: string, found: FoundCategories): string {
  const t = COPY[lang] ?? COPY.en!;
  const parts = (['analytics', 'marketing', 'functionality'] as const).filter((k) => found[k]).map((k) => t.parts[k]);
  if (parts.length === 0) return t.first;
  const list = parts.length === 1 ? parts[0]! : `${parts.slice(0, -1).join(', ')} ${t.and} ${parts[parts.length - 1]}`;
  return `${t.first} ${t.second.replace('{list}', list)}`;
}

/** "Say what the buttons do" preset. Bielova et al. 2024: the largest honest lever is consequence wording. */
export const PLAIN_LABELS: Record<string, { acceptAll: string; rejectAll: string }> = {
  en: { acceptAll: 'Allow tracking', rejectAll: 'Continue without tracking' },
  de: { acceptAll: 'Tracking erlauben', rejectAll: 'Ohne Tracking fortfahren' },
  fr: { acceptAll: 'Autoriser le suivi', rejectAll: 'Continuer sans suivi' },
  es: { acceptAll: 'Permitir el seguimiento', rejectAll: 'Continuar sin seguimiento' },
  it: { acceptAll: 'Consenti il tracciamento', rejectAll: 'Continua senza tracciamento' },
  nl: { acceptAll: 'Tracking toestaan', rejectAll: 'Doorgaan zonder tracking' },
};

/** Plain-wording buttons in every language we have. */
export function plainLabelsFor(lang: string): { acceptAll: string; rejectAll: string } | undefined {
  return PLAIN_LABELS[lang] ?? WORLD_PLAIN[lang] ?? PLAIN_LABELS[lang.slice(0, 2)] ?? WORLD_PLAIN[lang.slice(0, 2)];
}

// ---------- colour and brand ----------

export function hexToRgb(hex: string): [number, number, number] | null {
  const m = hex.trim().match(/^#?([0-9a-f]{6})$/i);
  if (!m) return null;
  const n = parseInt(m[1]!, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');
}

/** Accepts #rgb, #rrggbb, rgb()/rgba() with alpha >= 0.5. Returns #rrggbb or null. */
export function toHex(color: string): string | null {
  const c = color.trim().toLowerCase();
  const short = c.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/);
  if (short) return `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`;
  if (/^#[0-9a-f]{6}$/.test(c)) return c;
  const rgb = c.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/);
  if (rgb) {
    const a = rgb[4] === undefined ? 1 : Number(rgb[4]);
    if (a < 0.5) return null;
    return rgbToHex(Number(rgb[1]), Number(rgb[2]), Number(rgb[3]));
  }
  return null;
}

export function luminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const [r, g, b] = rgb.map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG 2.x contrast ratio, 1 to 21. */
export function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Black or white, whichever reads better on the given background. */
export function readableOn(bg: string): string {
  return contrast(bg, '#111827') >= contrast(bg, '#ffffff') ? '#111827' : '#ffffff';
}

/**
 * Text colours that read on their backgrounds (WCAG AA, 4.5:1), with the brand colours themselves
 * left exactly as set: where a text colour falls short, black or white (or a grey that passes, for
 * secondary text) takes its place. Returns which were changed.
 */
export function readableTheme<T extends { primary: string; onPrimary: string; background: string; text: string; muted: string }>(t: T): { theme: T; adjusted: ('onPrimary' | 'text' | 'muted')[] } {
  const AA = 4.5;
  const out = { ...t };
  const adjusted: ('onPrimary' | 'text' | 'muted')[] = [];
  if (contrast(out.onPrimary, out.primary) < AA) {
    out.onPrimary = readableOn(out.primary);
    adjusted.push('onPrimary');
  }
  if (contrast(out.text, out.background) < AA) {
    out.text = readableOn(out.background);
    adjusted.push('text');
  }
  if (contrast(out.muted, out.background) < AA) {
    const grey = luminance(out.background) > 0.5 ? '#4b5563' : '#d1d5db';
    out.muted = contrast(grey, out.background) >= AA ? grey : readableOn(out.background);
    adjusted.push('muted');
  }
  return { theme: out, adjusted };
}

export function saturation(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const max = Math.max(...rgb) / 255;
  const min = Math.min(...rgb) / 255;
  return max === 0 ? 0 : (max - min) / max;
}

/** Linear mix of two colours, t from 0 (a) to 1 (b). */
export function mix(a: string, b: string, t: number): string {
  const x = hexToRgb(a);
  const y = hexToRgb(b);
  if (!x || !y) return a;
  return rgbToHex(x[0] + (y[0] - x[0]) * t, x[1] + (y[1] - x[1]) * t, x[2] + (y[2] - x[2]) * t);
}

/** A button-like element seen on the page, with how often its style appeared. */
export interface BrandCandidate {
  background: string;
  color: string;
  radius: number;
  font: string;
  count: number;
  /** Visible text of one example, to help judge what the button is for. */
  sample: string;
}

/** What the scanner's browser read from the homepage. */
export interface BrandSignals {
  background: string;
  text: string;
  font: string;
  themeColor: string | null;
  candidates: BrandCandidate[];
  /** Index into candidates chosen as the brand's identity colour, and how sure the chooser was. */
  chosen: number | null;
  confidence: number | null;
}

/** Below this saturation a colour counts as neutral (white, grey, black) rather than a brand colour. */
export const NEUTRAL_SATURATION = 0.15;

/** Fallback when no chooser ran: the most saturated frequent colour; neutrals only when nothing else exists. */
export function pickCandidate(candidates: BrandCandidate[]): number | null {
  if (candidates.length === 0) return null;
  const vivid = candidates.filter((c) => saturation(toHex(c.background) ?? '#000000') >= NEUTRAL_SATURATION);
  const pool = vivid.length ? vivid : candidates;
  const scored = pool.map((c) => ({ i: candidates.indexOf(c), score: saturation(toHex(c.background) ?? '#000000') * 2 + Math.min(c.count, 5) / 5 }));
  scored.sort((a, b) => b.score - a.score);
  return scored[0]!.i;
}

/**
 * Site theme from brand signals. Contrast is enforced: button text and body text always meet
 * WCAG AA, swapping to black or white when the brand's own pairing does not.
 */
export function themeFromBrand(b: BrandSignals, base: SiteTheme = DEFAULT_THEME): { theme: SiteTheme; adjusted: string[] } {
  const adjusted: string[] = [];
  const idx = b.chosen ?? pickCandidate(b.candidates);
  const cand = idx === null ? null : b.candidates[idx] ?? null;
  const background = toHex(b.background) ?? base.background;
  let text = toHex(b.text) ?? base.text;
  if (contrast(text, background) < 4.5) {
    text = readableOn(background);
    adjusted.push('text');
  }
  let primary = cand ? toHex(cand.background) : null;
  const themeColor = toHex(b.themeColor ?? '');
  // A neutral pick (white or grey buttons) loses to a saturated theme-color: that is the identity colour.
  if ((!primary || saturation(primary) < NEUTRAL_SATURATION) && themeColor && saturation(themeColor) >= NEUTRAL_SATURATION) primary = themeColor;
  if (!primary) primary = base.primary;
  // Button labels are bold 14px: WCAG's large-text floor of 3:1 applies. Body text keeps 4.5:1.
  let onPrimary = cand ? toHex(cand.color) : null;
  if (!onPrimary || contrast(onPrimary, primary) < 3) {
    onPrimary = readableOn(primary);
    if (cand?.color) adjusted.push('onPrimary');
  }
  const radiusPx = cand ? Math.round(cand.radius) : 12;
  const radius = `${Math.max(0, Math.min(radiusPx > 24 ? 16 : radiusPx, 20))}px`;
  const font = (cand?.font || b.font || base.font).slice(0, 200);
  const muted = mix(text, background, 0.45);
  return { theme: { ...base, primary, onPrimary, background, text, muted, radius, font }, adjusted };
}

const ACTIONS: ReadonlySet<string> = new Set(['accept_all', 'reject_all', 'custom', 'gpc', 'implied']);
const ID_RE = /^[A-Za-z0-9_-]{8,64}$/;
const SITE_RE = /^[A-Za-z0-9_-]{3,64}$/;

function isRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null;
}

/** Strict validation of the client payload. Returns null on anything unexpected. */
export function validateLogPayload(x: unknown): LogPayload | null {
  if (!isRecord(x)) return null;
  const { site, id, ts, action, categories, version, lang, gpc, snapshot, bv } = x;
  if (typeof site !== 'string' || !SITE_RE.test(site)) return null;
  if (typeof id !== 'string' || !ID_RE.test(id)) return null;
  if (typeof ts !== 'number' || !Number.isFinite(ts) || ts < 1_600_000_000_000 || ts > 4_102_444_800_000) return null;
  if (typeof action !== 'string' || !ACTIONS.has(action)) return null;
  if (typeof version !== 'string' || version.length === 0 || version.length > 64) return null;
  if (typeof lang !== 'string' || !/^[a-z]{2}(-[A-Za-z]{2,4})?$/.test(lang)) return null;
  if (typeof gpc !== 'boolean') return null;
  if (!isRecord(categories)) return null;
  const choice = {} as ConsentChoice;
  for (const c of CATEGORIES) {
    const v = categories[c];
    if (typeof v !== 'boolean') return null;
    choice[c] = v;
  }
  if (!choice.necessary) return null;
  const out: LogPayload = { site, id, ts, action: action as ConsentAction, categories: choice, version, lang, gpc };
  if (typeof snapshot === 'string' && /^[a-f0-9]{8,64}$/.test(snapshot)) out.snapshot = snapshot;
  if (typeof bv === 'string' && /^\d+\.\d+\.\d+$/.test(bv)) out.bv = bv;
  return out;
}

/**
 * Snapshot id of a published config: SHA-256 over its JSON with the id itself left out, 20 hex
 * characters. Recomputing it from the stored copy shows the copy was not altered.
 */
export async function snapshotId(published: Record<string, unknown>): Promise<string> {
  const { snapshot: _s, ...rest } = published;
  const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(rest)));
  return Array.from(new Uint8Array(bytes), (b) => b.toString(16).padStart(2, '0')).join('').slice(0, 20);
}
